/**
 * Tripnovac Visa — Contact Us
 * Professional contact form with support channels
 *
 * @company Alpha Libra LLC
 * @domain tripnovac-visa.com
 */

import React, { useState } from 'react';
import {
  Box, Container, Typography, TextField, Button, MenuItem, Grid, Card,
  Paper, Avatar, Divider, CircularProgress, useTheme, alpha,
} from '@mui/material';
import {
  Email, Phone, LocationOn, AccessTime, Send, SupportAgent,
} from '@mui/icons-material';
import { APP_NAME, API_BASE_URL, EMAILS, COMPANY } from '../../constants';
import { useNotification } from '../../contexts/NotificationContext';

// =====================================================
// CONSTANTS
// =====================================================

const SUBJECT_OPTIONS = [
  'General Inquiry',
  'Visa Application Help',
  'Billing & Payments',
  'Technical Support',
  'Enterprise Sales',
  'Partnership Opportunity',
  'Bug Report',
  'Feature Request',
  'Other',
];

const CONTACT_CHANNELS = [
  {
    icon: <Email sx={{ fontSize: 32 }} />,
    title: 'Email',
    primary: EMAILS.SUPPORT,
    secondary: 'We respond within 24 hours',
    color: '#2563EB',
  },
  {
    icon: <Phone sx={{ fontSize: 32 }} />,
    title: 'Phone',
    primary: COMPANY.PHONE,
    secondary: 'Mon–Fri, 9am–6pm EST',
    color: '#059669',
  },
  {
    icon: <LocationOn sx={{ fontSize: 32 }} />,
    title: 'Office',
    primary: COMPANY.LOCATION,
    secondary: COMPANY.NAME,
    color: '#7C3AED',
  },
];

const BUSINESS_HOURS = [
  { day: 'Monday – Friday', hours: '9:00 AM – 6:00 PM EST' },
  { day: 'Saturday', hours: '10:00 AM – 2:00 PM EST' },
  { day: 'Sunday', hours: 'Closed' },
  { day: 'Holidays', hours: 'Limited Support (Email only)' },
];

// =====================================================
// COMPONENT
// =====================================================

const ContactPage: React.FC = () => {
  const theme = useTheme();
  const { showNotification } = useNotification();

  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name || !form.email || !form.subject || !form.message) {
      showNotification('Please fill in all fields.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/support/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error('Failed to send message');

      showNotification('Your message has been sent! We will get back to you shortly.', 'success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      showNotification('Something went wrong. Please try again or email us directly.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ── Hero Section ── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1E3A5F 0%, #0F2744 60%, #0A1929 100%)',
          color: '#fff',
          py: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 70% 40%, rgba(124,58,237,0.12) 0%, transparent 60%)',
          }}
        />
        <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
          <SupportAgent sx={{ fontSize: 56, mb: 2, opacity: 0.9 }} />
          <Typography variant="h3" fontWeight={800} gutterBottom>
            Contact Us
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.8, maxWidth: 600, mx: 'auto' }}>
            Have a question, feedback, or need assistance? We'd love to hear from you.
          </Typography>
        </Container>
      </Box>

      {/* ── Contact Info Cards ── */}
      <Container maxWidth="lg" sx={{ mt: -5, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3}>
          {CONTACT_CHANNELS.map((ch) => (
            <Grid item xs={12} sm={4} key={ch.title}>
              <Card
                elevation={4}
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 4,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: alpha(ch.color, 0.1),
                    color: ch.color,
                    width: 56,
                    height: 56,
                    mx: 'auto',
                    mb: 1.5,
                  }}
                >
                  {ch.icon}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700}>
                  {ch.title}
                </Typography>
                <Typography variant="body1" fontWeight={600} color="primary" sx={{ my: 0.5 }}>
                  {ch.primary}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ch.secondary}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Form + Business Hours ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={5}>
          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h5" fontWeight={800} gutterBottom>
                Send us a message
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }}>
                Fill out the form below and our team will respond within 24 hours.
              </Typography>

              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Full Name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      fullWidth
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': { bgcolor: '#fff', color: '#0F172A' },
                        '& .MuiInputBase-input': { color: '#0F172A' },
                        '& .MuiInputLabel-root': { color: '#475569' },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Address"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      fullWidth
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': { bgcolor: '#fff', color: '#0F172A' },
                        '& .MuiInputBase-input': { color: '#0F172A' },
                        '& .MuiInputLabel-root': { color: '#475569' },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Subject"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      select
                      fullWidth
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': { bgcolor: '#fff', color: '#0F172A' },
                        '& .MuiInputBase-input': { color: '#0F172A' },
                        '& .MuiInputLabel-root': { color: '#475569' },
                      }}
                    >
                      {SUBJECT_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt} sx={{ color: '#0F172A' }}>
                          {opt}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      multiline
                      rows={5}
                      fullWidth
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': { bgcolor: '#fff', color: '#0F172A' },
                        '& .MuiInputBase-input': { color: '#0F172A' },
                        '& .MuiInputLabel-root': { color: '#475569' },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={submitting}
                      endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Send />}
                      sx={{
                        px: 5,
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                      }}
                    >
                      {submitting ? 'Sending…' : 'Send Message'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={5}>
            {/* Business Hours */}
            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: `1px solid ${theme.palette.divider}`, mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Avatar sx={{ bgcolor: alpha('#D97706', 0.1), color: '#D97706' }}>
                  <AccessTime />
                </Avatar>
                <Typography variant="h6" fontWeight={700}>
                  Business Hours
                </Typography>
              </Box>
              {BUSINESS_HOURS.map((row, idx) => (
                <Box
                  key={row.day}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    py: 1.5,
                    borderBottom: idx < BUSINESS_HOURS.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                  }}
                >
                  <Typography fontWeight={600} variant="body2">
                    {row.day}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.hours}
                  </Typography>
                </Box>
              ))}
            </Paper>

            {/* Office Image */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'hidden',
                height: 220,
                backgroundImage: 'url(https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80&fit=crop)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-end',
                pb: 2,
              }}
            >
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 2, px: 2, py: 1, textAlign: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary">
                  Alpha Libra LLC
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Global Operations • Serving 190+ countries
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* ── Footer Note ── */}
      <Divider />
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} {APP_NAME} by Alpha Libra LLC. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default ContactPage;
